import { Box, Flex, HStack, Icon, Text } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import {
  Copy,
  FolderOpen,
  HelpCircle,
  MessageSquareText,
  PhoneCall,
  Printer,
} from 'lucide-react'
import React, { FC, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import IconIntegrationCurve from '~/assets/svgs/icon-integration-curve.svg?react'
import IconIntegrationDenticon from '~/assets/svgs/icon-integration-denticon.svg?react'
import IconIntegrationDentrix from '~/assets/svgs/icon-integration-dentrix.svg?react'
import IconIntegrationEagleSoft from '~/assets/svgs/icon-integration-eaglesoft.svg?react'
import IconIntegrationMango from '~/assets/svgs/icon-integration-mango.svg?react'
import IconIntegrationOpenDental from '~/assets/svgs/icon-integration-opendental.svg?react'
import {
  BlockedNumber,
  useBlockedNumbers,
  useFindBlockedNumber,
} from '~/atoms/BlockedNumbersState'
import { UnblockNumber } from '~/components/compositional/QuickActions'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { HighlightText } from '~/components/shared/HighlightText'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { ContactsOperation } from '~/hooks/toast/eventHandlers/contactsToastEventHandler'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { useEmitterService } from '~/hooks/useEmitterService'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { ContactSourceType } from '@mangovoice/types'

import { useContactItemStyle } from './componentStyles/ContactItem.style'
import { useContactItemsStyle } from './componentStyles/ContactItems.style'
import { useContactSourceStyle } from './componentStyles/ContactSource.style'
import {
  ContactActions,
  ContactDetails,
  ContactNumber,
} from './viewModels/Contacts.viewmodel'

const pmsMap: Record<
  ContactSourceType,
  { pmsName: string; Icon: FC<React.SVGProps<SVGSVGElement>> }
> = {
  [ContactSourceType.Curve]: {
    pmsName: 'Curve',
    Icon: IconIntegrationCurve,
  },
  [ContactSourceType.Denticon]: {
    pmsName: 'Denticon',
    Icon: IconIntegrationDenticon,
  },
  [ContactSourceType.Dentrix]: {
    pmsName: 'Dentrix',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.DentrixAscend]: {
    pmsName: 'Dentrix Ascend',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.DentrixG7]: {
    pmsName: 'Dentrix G7',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.DentrixEnterprise]: {
    pmsName: 'Dentrix Enterprise',
    Icon: IconIntegrationDentrix,
  },
  [ContactSourceType.Eaglesoft]: {
    pmsName: 'EagleSoft',
    Icon: IconIntegrationEagleSoft,
  },
  [ContactSourceType.OpenDental]: {
    pmsName: 'Open Dental',
    Icon: IconIntegrationOpenDental,
  },
  [ContactSourceType.Extensions]: {
    pmsName: 'Mango',
    Icon: IconIntegrationMango,
  },
  [ContactSourceType.Pbxes]: { pmsName: 'Mango', Icon: IconIntegrationMango },
  [ContactSourceType.Unknown]: {
    pmsName: 'Unknown',
    Icon: HelpCircle,
  },
}

const tryGetPmsRecordOrDefault = (pms: ContactSourceType) => {
  if (pms in pmsMap) {
    return pmsMap[pms]
  }

  return pmsMap[ContactSourceType.Unknown]
}
interface ContactSourceProps {
  sourceName: string
  pms: ContactSourceType
}
const ContactSource = ({ sourceName, pms }: ContactSourceProps) => {
  const { pmsName, Icon: pmsIcon } = tryGetPmsRecordOrDefault(pms)
  const { t } = useTranslation()
  const styles = useContactSourceStyle()

  return (
    // Source Container
    <Flex css={styles.container}>
      {/* Source Label */}
      <Text css={styles.labelText}>{t('contactItems.contactSource')}</Text>
      {/* Source Data Container */}
      <Flex css={styles.dataContainer}>
        {/* Source Icon Container */}
        <Flex css={styles.iconContainer}>
          {/* Source Icon */}
          <Icon css={styles.icon} as={pmsIcon} />
        </Flex>
        {/* Source Details Container */}
        <Flex direction="column">
          {/* Source Name */}
          <Text css={styles.nameText}>{sourceName}</Text>
          {/* Source PMS */}
          <Text css={styles.pmsText}>
            {t('contactItems.fromPms', { pmsName })}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

interface ActionButtonProps {
  label: string
  ariaLabel: string
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  onClick: () => void
}
const ActionButton = forwardRef(function ActionButton(
  { label, ariaLabel, icon, onClick: handleClick }: ActionButtonProps,
  ref: React.Ref<HTMLButtonElement> | undefined,
) {
  return (
    <QuickActionButton
      ref={ref}
      variant={QuickActionButtonVariants.Default_100}
      size={Size.sm}
      actionIcon={icon}
      tooltipText={label}
      ariaLabel={ariaLabel}
      onClick={handleClick}
    />
  )
})

const getActionButton = (
  index: number,
  action: ContactActions,
  onClick: () => void,
  number: string,
  t: TFunction,
) => {
  switch (action) {
    case 'call':
      return (
        <ActionButton
          key={index}
          label={t('contactItems.actionButton.callNumber')}
          ariaLabel={t('contactItems.actionButton.callNumberLabel', {
            number,
          })}
          icon={PhoneCall}
          onClick={onClick}
        />
      )
    case 'message':
      return (
        <ActionButton
          key={index}
          label={t('contactItems.actionButton.messageNumber')}
          ariaLabel={t('contactItems.actionButton.messageNumberLabel', {
            number,
          })}
          icon={MessageSquareText}
          onClick={onClick}
        />
      )
    case 'fax':
      return (
        <ActionButton
          key={index}
          label={t('contactItems.actionButton.sendFax')}
          ariaLabel={t('contactItems.actionButton.sendFaxLabel', { number })}
          icon={Printer}
          onClick={onClick}
        />
      )
  }
}

interface ContactItemProps {
  key?: string | number
  contactDetails: ContactDetails
  contactNumber: ContactNumber
  searchTerm?: string
  blockedNumbers?: BlockedNumber[]
  onCallClick?: (callee: {
    contactNumber: ContactNumber
    contact: ContactDetails
  }) => void
  onMessagingClick?: (number: string) => void
  onFaxClick?: (number: string) => void
  onCopyClick?: (number: string) => void
  onUnblockClick?: (number: BlockedNumber) => void
}
const ContactItem = ({
  contactNumber,
  contactDetails,
  searchTerm = '',
  blockedNumbers,
  onCallClick,
  onMessagingClick,
  onFaxClick,
  onCopyClick,
  onUnblockClick,
}: ContactItemProps) => {
  const { emit } = useEmitterService()
  const { t } = useTranslation()
  const showTexting = useShowTexting()

  const handleCallStartClick = () => {
    onCallClick?.({ contactNumber, contact: contactDetails })
  }
  const handleMessagingClick = () => {
    onMessagingClick?.(contactNumber.number)
  }
  const handleFaxClick = () => {
    onFaxClick?.(contactNumber.number)
  }
  const handleCopyClick = () => {
    onCopyClick?.(formatPhoneNumber(contactNumber.number))
    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: NotificationDisposition.Success,
      domain: NotificationDomain.CONTACTS,
      operation: ContactsOperation.ContactNumberCopiedToClipboard,
    })
  }
  const actionHandlerMap: Record<ContactActions, () => void> = {
    call: handleCallStartClick,
    message: handleMessagingClick,
    fax: handleFaxClick,
  }

  const blockedNumber = useFindBlockedNumber(
    blockedNumbers,
    contactNumber.number,
  )
  const handleUnblockClick = () => {
    if (!blockedNumber) {
      return
    }
    onUnblockClick?.(blockedNumber)
  }

  const displayNumber = contactNumber.isExtension
    ? contactNumber.number
    : formatPhoneNumber(contactNumber.number)

  const styles = useContactItemStyle()
  return (
    <Flex css={styles.container}>
      <Flex css={styles.dataContainer}>
        {!contactNumber.isExtension && (
          <Flex css={styles.labelContainer}>
            <Text css={styles.labelText}>
              {toNounCasing(contactNumber.label)}
            </Text>
            {contactNumber.isPrimary && (
              <Flex css={styles.primaryBadge}>{t('contactItems.primary')}</Flex>
            )}
          </Flex>
        )}
        {contactNumber.isExtension && (
          <ExtensionBadge
            size={Size.xl}
            extension={contactNumber.number}
            searchTerm={searchTerm}
          />
        )}
        {!contactNumber.isExtension && (
          <HighlightText
            css={styles.numberText}
            query={[searchTerm]}
            permutationFilters={['-', ' ', '(', ')']}
          >
            {formatPhoneNumber(contactNumber.number)}
          </HighlightText>
        )}{' '}
      </Flex>
      {/* Action Stack */}
      <HStack gap="0.25rem">
        {blockedNumber && (
          <UnblockNumber
            size={Size.sm}
            variant={QuickActionButtonVariants.Destructive_100}
            onClick={handleUnblockClick}
            ariaLabel={t('contactItems.actionButton.unblockNumberLabel', {
              number: displayNumber,
            })}
          />
        )}
        {contactNumber.availableActions
          .filter((action) => showTexting || action !== 'message')
          .map((action, index) =>
            getActionButton(
              index,
              action,
              actionHandlerMap[action],
              displayNumber,
              t,
            ),
          )}
        <ActionButton
          label={t('contactItems.actionButton.copyNumber')}
          ariaLabel={t('contactItems.actionButton.copyNumberLabel', {
            number: displayNumber,
          })}
          icon={Copy}
          onClick={handleCopyClick}
        />
      </HStack>
    </Flex>
  )
}
const EmptyContactItem = () => {
  const styles = useContactItemStyle()
  const { t } = useTranslation()
  return (
    <Flex css={styles.empty_container}>
      <Icon as={FolderOpen} css={styles.empty_icon} />
      <Text css={styles.empty_text}>{t('contactItems.emptyContactItem')}</Text>
    </Flex>
  )
}

interface ContactItemsProps {
  contactDetails: ContactDetails
  searchTerm?: string
  onCallClick: (callee: {
    contactNumber: ContactNumber
    contact: ContactDetails
  }) => void
  onMessagingClick: (number: string) => void
  onFaxClick: (number: string) => void
  onCopyClick: (number: string) => void
}
export const ContactItems = ({
  contactDetails,
  searchTerm,
  onCallClick,
  onMessagingClick,
  onFaxClick,
  onCopyClick,
}: ContactItemsProps) => {
  const styles = useContactItemsStyle()
  const { t } = useTranslation()
  const { blockedNumbers, unblockNumber } = useBlockedNumbers()

  const handleUnblockNumber = (number: BlockedNumber) => {
    unblockNumber(number.id, number.phoneNumber)
  }

  // Extract layout properties from styles to apply as native props
  const containerLayoutProps = {
    flexDirection: styles.container?.flexDirection,
    height: styles.container?.height,
    flex: styles.container?.flex,
    paddingX: styles.container?.paddingX,
    paddingTop: styles.container?.paddingTop,
    paddingBottom: styles.container?.paddingBottom,
  }

  const textLayoutProps = {
    width: styles.text?.width,
    paddingBottom: styles.text?.paddingBottom,
  }

  const scrollContainerLayoutProps = {
    overflowY: styles.scrollContainer?.overflowY,
  }

  return (
    <Flex css={styles.container} {...containerLayoutProps}>
      <Text as="h2" css={styles.text} {...textLayoutProps}>
        {t('contactItems.contactInformation')}
      </Text>
      <Box
        css={styles.scrollContainer}
        {...scrollContainerLayoutProps}
        className="scrollbar-hidden"
      >
        {contactDetails?.numbers?.map((contactNumber, index) => {
          return (
            <ContactItem
              key={index}
              contactNumber={contactNumber}
              contactDetails={contactDetails}
              blockedNumbers={blockedNumbers}
              searchTerm={searchTerm}
              onCallClick={onCallClick}
              onMessagingClick={onMessagingClick}
              onFaxClick={onFaxClick}
              onCopyClick={onCopyClick}
              onUnblockClick={handleUnblockNumber}
            />
          )
        }) ?? <EmptyContactItem />}
        <ContactSource
          sourceName={contactDetails?.sourceName}
          pms={contactDetails?.source}
        />
      </Box>
    </Flex>
  )
}
